<template>
    <section class="px-4 py-6 mx-auto max-w-5xl lg:py-6 md-max:pt-0">
        <div>
            <h2 class="text-2xl text-center xl:leading-10 font-ww-bold xl:text-42 md:text-3xl" v-html="mainTitle"></h2>

            <div class="mt-4 text-base text-center md:mt-6 lg:mt-10 xl:text-xl md:text-lg font-ww-regular" v-html="subtitle"></div>

            <div class="grid grid-cols-12 gap-x-3 gap-y-6 mt-6 md:mt-12 sm:gap-x-6 md:px-5 sm:px-4 md:gap-y-6 lg:gap-y-10 xl:gap-x-10">
                <div v-for="(item, index) in normalizedItems" :key="index" class="col-span-6 md:col-span-4" :style="{ backgroundColor: cardBgColor }">
                    <div>
                        <nuxt-img 
                            :src="item.imageSrc" 
                            :alt="item.imageAlt" 
                            width="301"
                            height="192"
                            format="webp"
                            loading="lazy"
                            sizes="xs:140px sm:191px md:329px lg:296px xl:301px 2xl:301px"
                            class="w-full"
                        />
                        <div class="overflow-hidden -mt-16" aria-hidden="true">
                            <svg class="" style="width: 101%;" width="400" height="115.6" viewBox="0 0 301 87" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M163 39.5146L301 0V87H163V39.5146Z" fill="url(#paint0_linear_2754_2065)" />
    <path d="M301 50.9766L0 14V87H301V50.9766Z" fill="#EBDFD3" />
    <defs>
        <linearGradient id="paint0_linear_2754_2065" x1="301" y1="43.5" x2="163" y2="43.5" gradientUnits="userSpaceOnUse">
            <stop stop-color="#D2F06F" />
            <stop offset="1" stop-color="#798A40" />
        </linearGradient>
    </defs>
</svg>

                        </div>

                        <div class="px-4 pb-10 -mt-10 lg:-mt-8 lg:px-7 md:px-6">
                            <h3 class="mb-1 text-lg text-center xl:text-xl font-ww-bold md-max:leading-5" v-html="item.title"></h3>

                            <div class="text-base text-center xl:text-xl sm:text-lg font-ww-regular" v-html="item.description"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-6 text-base text-center md:mt-12 xl:text-xl md:text-lg font-ww-regular" v-html="closingText"></div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  mainTitle: {
    type: String,
    default: 'How It Works'
  },
  subtitle: {
    type: String,
    default: 'Each ingredient plays a specific role—like instruments in an orchestra, all working in harmony:'
  },
  cardBgColor: {
    type: String,
    default: '#EBDFD3'
  },
  items: {
    type: Array,
    default: () => [
      {
        imageSrc: '/assets/images/grid-mid-1.jpg',
        imageAlt: 'Organic Arabica Coffee',
        title: 'Organic Arabica Coffee',
        description: 'Smooth, natural caffeine to spark your brain into action—without the harsh edge of synthetic sources.'
      },
      {
        imageSrc: '/assets/images/grid-mid-2.jpg',
        imageAlt: 'Alpha GPC',
        title: 'Alpha GPC',
        description: 'Channels that spark into concentration by supporting acetylcholine, the neurotransmitter tied to focus and learning.'
      },
      {
        imageSrc: '/assets/images/grid-mid-3.jpg',
        imageAlt: "Lion's Mane and Chaga",
        title: "Lion's Mane and Chaga",
        description: 'Helps the brain stay sharp by supporting healthy nerve growth, long-term cognitive resilience, and calm, steady energy by helping to manage oxidative stress.'
      },
      {
        imageSrc: '/assets/images/grid-mid-4.jpg',
        imageAlt: 'Cognitiq®',
        title: 'Cognitiq®',
        description: 'A patented botanical shown in clinical studies to increase BDNF—your brain\'s "fertilizer" for learning and mental stamina.'
      },
      {
        imageSrc: '/assets/images/grid-mid-5.jpg',
        imageAlt: 'Electroprime™',
        title: 'Electroprime™',
        description: 'Hydration matters: electrolytes offset the dehydrating effects of caffeine, keeping your mind clear and your body balanced.'
      },
      {
        imageSrc: '/assets/images/grid-mid-6.jpg',
        imageAlt: 'Grass-Fed Collagen',
        title: 'Grass-Fed Collagen',
        description: 'Supports a healthy gut-brain connection and structural foundation, because focus starts from within.'
      }
    ]
  },
  closingText: {
    type: String,
    default: 'Together, these ingredients transform caffeine from a quick jolt…<br><br>…into <strong>smooth, long lasting performance fuel for getting things done.</strong>'
  }
})

const normalizedItems = computed(() => {
  return props.items.map((item: any) => ({
    imageSrc: item.imageSrc || '',
    imageAlt: item.imageAlt || '',
    title: item.title || '',
    description: item.description || ''
  }))
})

defineBuilderComponent({
  friendlyName: 'Grid With Info',
  inputs: [
    {
      name: 'mainTitle',
      type: 'richText',
      defaultValue: 'How It Works'
    },
    {
      name: 'subtitle',
      type: 'richText',
      defaultValue: 'Each ingredient plays a specific role—like instruments in an orchestra, all working in harmony:'
    },
    {
      name: 'cardBgColor',
      type: 'color',
      defaultValue: '#EBDFD3'
    },
    {
      name: 'items',
      type: 'list',
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          required: true
        },
        {
          name: 'imageAlt',
          type: 'text',
          required: true
        },
        {
          name: 'title',
          type: 'richText',
          required: true
        },
        {
          name: 'description',
          type: 'richText',
          required: true
        }
      ],
      defaultValue: [
        {
          imageSrc: '/assets/images/grid-mid-1.jpg',
          imageAlt: 'Organic Arabica Coffee',
          title: 'Organic Arabica Coffee',
          description: 'Smooth, natural caffeine to spark your brain into action—without the harsh edge of synthetic sources.'
        },
        {
          imageSrc: '/assets/images/grid-mid-2.jpg',
          imageAlt: 'Alpha GPC',
          title: 'Alpha GPC',
          description: 'Channels that spark into concentration by supporting acetylcholine, the neurotransmitter tied to focus and learning.'
        },
        {
          imageSrc: '/assets/images/grid-mid-3.jpg',
          imageAlt: "Lion's Mane and Chaga",
          title: "Lion's Mane and Chaga",
          description: 'Helps the brain stay sharp by supporting healthy nerve growth, long-term cognitive resilience, and calm, steady energy by helping to manage oxidative stress.'
        },
        {
          imageSrc: '/assets/images/grid-mid-4.jpg',
          imageAlt: 'Cognitiq®',
          title: 'Cognitiq®',
          description: 'A patented botanical shown in clinical studies to increase BDNF—your brain\'s "fertilizer" for learning and mental stamina.'
        },
        {
          imageSrc: '/assets/images/grid-mid-5.jpg',
          imageAlt: 'Electroprime™',
          title: 'Electroprime™',
          description: 'Hydration matters: electrolytes offset the dehydrating effects of caffeine, keeping your mind clear and your body balanced.'
        },
        {
          imageSrc: '/assets/images/grid-mid-6.jpg',
          imageAlt: 'Grass-Fed Collagen',
          title: 'Grass-Fed Collagen',
          description: 'Supports a healthy gut-brain connection and structural foundation, because focus starts from within.'
        }
      ]
    },
    {
      name: 'closingText',
      type: 'richText',
      defaultValue: 'Together, these ingredients transform caffeine from a quick jolt…<br><br>…into <strong>smooth, long lasting performance fuel for getting things done.</strong>'
    }
  ]
})
</script>